import { useEffect, useMemo, useState } from "react";
import {
  type GameLibrarySummary,
  type GameListFilters,
  type GameListItem,
  type GameStreamProgress,
  importChessComGames,
} from "./games.api";
import { useGamesStore } from "./games.store";
import { useAuth } from "@/app/context/AuthContext";

const TIME_CONTROL_LABEL: Record<string, string> = {
  bullet: "Bullet",
  blitz: "Blitz",
  rapid: "Rapid",
  daily: "Daily",
};

export interface GroupedGames {
  timeControl: string;
  label: string;
  games: GameListItem[];
}

export interface UseGamesReturn {
  games: GameListItem[];
  summary: GameLibrarySummary | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreGames: boolean;
  streamProgress: GameStreamProgress | null;
  isImporting: boolean;
  importStatus: string | null;
  search: string;
  setSearch: (value: string) => void;
  filtered: GameListItem[];
  grouped: GroupedGames[];
  handleImport: () => Promise<void>;
  refreshGames: () => Promise<void>;
  loadMoreGames: () => Promise<void>;
  loadGamesForSelection: (filters: GameListFilters, force?: boolean) => Promise<void>;
  loadRemainingGames: () => Promise<void>;
  markGameAnalyzed: (
    gameId: string,
    result: {
      accuracy: number;
      averageCpl: number;
      blunderCount: number;
      mistakeCount: number;
      analysisDepth: number;
    },
  ) => void;
}

export function useGames(initialSearch = "", initialFilters?: GameListFilters): UseGamesReturn {
  const { userId } = useAuth();
  const ownerUserId = useGamesStore((state) => state.ownerUserId);
  const ownedGames = useGamesStore((state) => state.games);
  const ownedSummary = useGamesStore((state) => state.summary);
  const ownedIsLoading = useGamesStore((state) => state.isLoading);
  const setOwner = useGamesStore((state) => state.setOwner);
  const isCurrentOwner = ownerUserId === userId;
  const cachedGames = isCurrentOwner ? ownedGames : null;
  const summary = isCurrentOwner ? ownedSummary : null;
  const isLoading = !isCurrentOwner || ownedIsLoading;
  const isLoadingMore = useGamesStore((state) => state.isLoadingMore);
  const hasMoreGames = useGamesStore((state) => state.hasMoreGames);
  const streamProgress = useGamesStore((state) => state.streamProgress);
  const activeFilters = useGamesStore((state) => state.filters);
  const loadGames = useGamesStore((state) => state.loadGames);
  const loadGamesForSelection = useGamesStore((state) => state.loadGamesForSelection);
  const loadMoreGames = useGamesStore((state) => state.loadMoreGames);
  const loadRemainingGames = useGamesStore((state) => state.loadRemainingGames);
  const refreshGames = useGamesStore((state) => state.refreshGames);
  const markGameAnalyzed = useGamesStore((state) => state.markGameAnalyzed);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [search, setSearch] = useState(initialSearch);
  const games = useMemo(() => cachedGames ?? [], [cachedGames]);

  useEffect(() => {
    setOwner(userId);
    if (initialFilters) {
      const hasCachedSelection =
        cachedGames !== null &&
        activeFilters.analyzed === initialFilters.analyzed &&
        activeFilters.timeControl === initialFilters.timeControl &&
        (initialFilters.exactTimeControl == null ||
          activeFilters.exactTimeControl === initialFilters.exactTimeControl) &&
        activeFilters.period === initialFilters.period &&
        activeFilters.from === initialFilters.from &&
        activeFilters.to === initialFilters.to;
      if (hasCachedSelection) return;
      void loadGamesForSelection(initialFilters);
      return;
    }
    void loadGames();
  }, [
    activeFilters.analyzed,
    activeFilters.exactTimeControl,
    activeFilters.timeControl,
    activeFilters.period,
    activeFilters.from,
    activeFilters.to,
    cachedGames,
    initialFilters,
    loadGames,
    loadGamesForSelection,
    setOwner,
    userId,
  ]);

  const handleImport = async () => {
    setIsImporting(true);
    setImportStatus(null);
    try {
      const result = await importChessComGames();
      setImportStatus(`Imported ${result.gamesImported} games`);
      await refreshGames();
    } catch {
      setImportStatus("Import failed — check your Chess.com username");
    } finally {
      setIsImporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return games;
    return games.filter((g) => g.opponent.toLowerCase().includes(q) || (g.eco?.toLowerCase().includes(q) ?? false));
  }, [games, search]);

  const grouped = useMemo<GroupedGames[]>(() => {
    const byTimeControl = new Map<string, GameListItem[]>();
    for (const game of filtered) {
      const key = game.timeControl;
      if (!byTimeControl.has(key)) byTimeControl.set(key, []);
      byTimeControl.get(key)!.push(game);
    }
    return [...byTimeControl.keys()]
      .sort((a, b) => byTimeControl.get(b)!.length - byTimeControl.get(a)!.length)
      .map((key) => ({
        timeControl: key,
        label: TIME_CONTROL_LABEL[key] ?? key,
        games: byTimeControl.get(key)!,
      }));
  }, [filtered]);

  return {
    games,
    summary,
    isLoading,
    isLoadingMore,
    hasMoreGames,
    streamProgress,
    isImporting,
    importStatus,
    search,
    setSearch,
    filtered,
    grouped,
    handleImport,
    refreshGames,
    loadMoreGames,
    loadRemainingGames,
    loadGamesForSelection,
    markGameAnalyzed,
  };
}
